import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Divider,
  Grid,
  Paper,
  Avatar
} from "@mui/material";
import { Volunteer } from "../types/volunteer";

interface Props {
  volunteer: Volunteer;
}

const VolunteerDetail: React.FC<Props> = ({ volunteer }) => {
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {volunteer.name}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            {volunteer.profile_image_url ? (
              <CardMedia
                component="img"
                height="300"
                image={volunteer.profile_image_url}
                alt={volunteer.name}
                sx={{ objectFit: 'cover' }}
              />
            ) : (
              <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
                <Avatar sx={{ width: 120, height: 120, fontSize: '3rem' }}>
                  {volunteer.name.charAt(0).toUpperCase()}
                </Avatar>
              </Box>
            )}
            <CardContent>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip 
                  label={volunteer.status} 
                  color={volunteer.status === 'active' ? 'success' : 'default'}
                  size="medium"
                />
                <Chip 
                  label={volunteer.availability === "1" ? 'Available' : 'Not Available'} 
                  color={volunteer.availability === "1" ? 'primary' : 'default'}
                  size="medium"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Contact Information
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1">
                  {volunteer.email}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Phone
                </Typography>
                <Typography variant="body1">
                  {volunteer.phone}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" gutterBottom>
              Skills
            </Typography>
            <Typography variant="body1" paragraph>
              {volunteer.skills || 'No skills specified'}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" gutterBottom>
              Assigned Projects ({volunteer.projects?.length || 0})
            </Typography>
            {volunteer.projects && volunteer.projects.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                {volunteer.projects.map((project) => (
                  <Card key={project.id} variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {project.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {project.description}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip 
                          label={project.status} 
                          color={project.status === 'active' ? 'success' : 'default'}
                          size="small"
                        />
                        {project.pivot.role && (
                          <Chip 
                            label={`Role: ${project.pivot.role}`} 
                            color="primary"
                            size="small"
                          />
                        )}
                        <Chip 
                          label={`Assigned: ${new Date(project.pivot.assigned_at).toLocaleDateString()}`} 
                          color="info"
                          size="small"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                No projects assigned yet.
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" gutterBottom>
              Details
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Created At
                </Typography>
                <Typography variant="body2">
                  {new Date(volunteer.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Last Updated
                </Typography>
                <Typography variant="body2">
                  {new Date(volunteer.updated_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default VolunteerDetail;
